// "Due for Review" — the quiet spaced-repetition surface.
//
// This is the ONE place Leitner-lite scheduling shows its face. It reads the
// signed-in user's workspace, finds the cards that are due (or pinned and never
// reviewed — those are new cards), and walks them one flip card at a time. Each
// rating ("Still learning" / "I knew it") rolls the card's schedule forward via
// lib/srs and persists it; the card simply leaves the session.
//
// Staying true to Azzurro's no-gamification rule: no score, no streak, no timer,
// no "N-day run". Just what's due, and a calm 🌿 panel when you're caught up —
// with a gentle note about when the next batch comes back.
import { isAuthed } from "../lib/auth.js";
import { fetchVerbs, fetchVocabulary, fetchWorkspace, recordReview } from "../lib/data.js";
import { reviewQueue, nextDueDate } from "../lib/srs.js";
import { escapeHtml } from "../ui/helpers.js";

// Turn a workspace row into a flippable card by resolving its item_id against
// the public datasets. Only verbs and vocabulary are flashcard-shaped; pinned
// articles are skipped here (they live in the Reference shelf, not review).
function resolveCard(row, verbsById, vocabById) {
  if (row.collection_type === "verbs") {
    const v = verbsById.get(row.item_id);
    if (!v) return null;
    return {
      collection_type: "verbs",
      item_id: row.item_id,
      front: v.infinitive,
      back: v.english_translation || "",
      emoji: v.emoji || "",
      sub: "verb",
    };
  }
  if (row.collection_type === "vocabulary") {
    const c = vocabById.get(row.item_id);
    if (!c) return null;
    return {
      collection_type: "vocabulary",
      item_id: row.item_id,
      front: c.spanish,
      back: c.english || "",
      emoji: c.emoji || "",
      exEs: c.example_es || "",
      exEn: c.example_en || "",
      sub: c.theme || "vocabulary",
    };
  }
  return null;
}

export function reviewView(app) {
  app.innerHTML = `
    <section>
      <div class="mb-5">
        <h2 class="text-2xl font-bold text-sage-800">Due for Review</h2>
        <p class="text-sage-600 text-sm mt-1 max-w-2xl">A small, spaced rotation of the words and verbs you've pinned. Cards you find easy come back less often; the ones you're still learning return sooner. No scores — just what's worth a second look today.</p>
      </div>
      <div id="review-stage"></div>
    </section>`;

  const stage = app.querySelector("#review-stage");

  if (!isAuthed()) {
    stage.innerHTML = `
      <div class="max-w-2xl bg-white border border-sage-200 rounded-2xl p-6">
        <p class="text-3xl mb-2">🌿</p>
        <p class="text-lg font-semibold text-sage-800">Spaced review is personal</p>
        <p class="text-sage-600 text-sm mt-1">Sign in (top-right) to build a review rotation. Pin any verb or vocabulary word and it'll start showing up here, spaced out over time so it actually sticks.</p>
      </div>`;
    return;
  }

  stage.innerHTML = `<p class="text-sage-500 text-sm">Gathering what's due…</p>`;

  let session = { queue: [], idx: 0, flipped: false };

  Promise.all([fetchVerbs(), fetchVocabulary(), fetchWorkspace()])
    .then(([verbs, vocab, workspace]) => {
      const verbsById = new Map(verbs.map((v) => [v.id, v]));
      const vocabById = new Map(vocab.map((c) => [c.id, c]));

      const due = reviewQueue(workspace);
      const cards = due.map((r) => resolveCard(r, verbsById, vocabById)).filter(Boolean);

      session.queue = cards;
      session.idx = 0;
      session.flipped = false;

      if (!cards.length) {
        renderEmpty(stage, workspace);
        return;
      }
      renderCard(stage, session);
    })
    .catch(() => {
      stage.innerHTML = `
        <div class="max-w-2xl bg-white border border-sage-200 rounded-2xl p-6">
          <p class="text-3xl mb-2">🌿</p>
          <p class="text-lg font-semibold text-sage-800">Couldn't load your review set</p>
          <p class="text-sage-600 text-sm mt-1">The backend wasn't reachable just now. Try again in a moment.</p>
        </div>`;
    });

  window.scrollTo({ top: 0, behavior: "smooth" });
}

function renderEmpty(stage, workspace) {
  const next = nextDueDate(workspace);
  const hasAny = (workspace || []).some((w) => w.is_pinned || w.srs_due_at);
  const whenLine = next
    ? `Next batch comes back ${escapeHtml(next.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" }))}.`
    : hasAny
    ? "Nothing waiting right now."
    : "Pin a verb or vocabulary word and it'll start showing up here.";

  stage.innerHTML = `
    <div class="max-w-2xl text-center py-12">
      <p class="text-4xl mb-3">🌿</p>
      <p class="text-lg font-semibold text-sage-800">You're caught up</p>
      <p class="text-sage-600 text-sm mt-1">${whenLine}</p>
    </div>`;
}

function cardFaceHTML(card, side) {
  const text = side === "front" ? card.front : card.back;
  const emoji = card.emoji
    ? `<span class="text-4xl mb-2" aria-hidden="true">${card.emoji}</span>`
    : "";
  const ex =
    side === "back" && card.exEs
      ? `<div class="mt-3 text-center">
           <p class="text-sm text-sage-700">${escapeHtml(card.exEs)}</p>
           ${card.exEn ? `<p class="text-xs text-sage-500 italic mt-0.5">${escapeHtml(card.exEn)}</p>` : ""}
         </div>`
      : "";
  return `<div class="flex flex-col items-center justify-center text-center w-full px-2">${emoji}<p class="text-3xl sm:text-4xl font-bold text-sage-800">${escapeHtml(
    text
  )}</p>${ex}</div>`;
}

function renderCard(stage, session) {
  if (session.idx >= session.queue.length) {
    stage.innerHTML = `
      <div class="max-w-2xl text-center py-12">
        <p class="text-4xl mb-3">🌿</p>
        <p class="text-lg font-semibold text-sage-800">That's the batch</p>
        <p class="text-sage-600 text-sm mt-1">You worked through everything due today. Each card has a fresh date now — come back when the next one's ready.</p>
      </div>`;
    return;
  }

  const card = session.queue[session.idx];
  const total = session.queue.length;

  stage.innerHTML = `
    <div class="max-w-2xl bg-white rounded-2xl border border-sage-100 p-6">
      <div class="flex justify-between text-xs text-sage-400 mb-4">
        <span class="uppercase tracking-wide">${escapeHtml(card.sub)}</span>
        <span>${session.idx + 1} / ${total} due</span>
      </div>
      <div id="rev-card" class="flash-card cursor-pointer mb-5">
        <div class="flash-inner relative h-56">
          <div class="flash-face absolute inset-0 bg-gradient-to-br from-sage-50 to-sand-50 border border-sage-200 rounded-2xl flex items-center justify-center p-4">
            ${cardFaceHTML(card, "front")}
          </div>
          <div class="flash-face flash-back absolute inset-0 bg-gradient-to-br from-sage-100 to-sage-50 border border-sage-300 rounded-2xl flex items-center justify-center p-4 overflow-auto">
            ${cardFaceHTML(card, "back")}
          </div>
        </div>
      </div>
      <p class="text-center text-xs text-sage-400 mb-4" id="rev-hint">Tap card to flip</p>
      <div class="grid grid-cols-2 gap-3">
        <button id="rev-learning" class="bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-4 py-3 rounded-xl font-medium">
          ✗ Still learning
        </button>
        <button id="rev-known" class="bg-sage-100 hover:bg-sage-200 text-sage-700 border border-sage-300 px-4 py-3 rounded-xl font-medium">
          ✓ I knew it
        </button>
      </div>
    </div>`;

  const cardEl = stage.querySelector("#rev-card");
  cardEl.addEventListener("click", () => {
    session.flipped = !session.flipped;
    cardEl.classList.toggle("flash-flipped");
    stage.querySelector("#rev-hint").textContent = session.flipped
      ? "Tap card to flip back"
      : "Tap card to flip";
  });

  stage.querySelector("#rev-known").addEventListener("click", () => rate(stage, session, "known"));
  stage.querySelector("#rev-learning").addEventListener("click", () => rate(stage, session, "learning"));
}

function rate(stage, session, rating) {
  const card = session.queue[session.idx];
  // Persist the schedule roll-forward in the background; the UI advances
  // immediately so a momentary backend hiccup never blocks the session.
  recordReview(card.collection_type, card.item_id, rating).catch(() => {});
  session.idx++;
  session.flipped = false;
  renderCard(stage, session);
}
